import React from 'react';
import { Building2 } from 'lucide-react';
import type { Brand } from '../../types';
import { useTranslation } from 'react-i18next';

interface BrandCardProps {
  selectedBrand: string;
  onBrandSelect: (brandId: string) => void;
  isLoading?: boolean;
  brands: Brand[];
}

const BrandGrid: React.FC<{ brands: Brand[]; selectedBrand: string; onBrandSelect: (brandId: string) => void }> = ({
  brands,
  selectedBrand,
  onBrandSelect
}) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-2">
    {brands.map(brand => (
      <button
        key={brand.id}
        onClick={() => onBrandSelect(brand.id)}
        className={`flex flex-col items-center justify-center gap-2 p-2 h-28 w-full rounded-lg border border-black ${
          selectedBrand === brand.id
            ? 'border-primary-600 bg-primary-50'
            : 'hover:border-primary-600'
        }`}
      >
        <img 
          src={brand.logo} 
          alt={brand.name} 
          className="w-8 h-8 rounded-lg"
        />
        <span className="text-xs text-gray-700 text-center line-clamp-2 overflow-hidden">{brand.name}</span>
      </button>
    ))}
  </div>
);

export const BrandCard: React.FC<BrandCardProps> = ({
  selectedBrand,
  isLoading = false,
  onBrandSelect,
  brands
}) => {
  const { t } = useTranslation();
  const showGrid = brands.length <= 9;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Building2 className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">{t('cards.brands.title', 'Brands')}</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex flex-col items-center gap-2 p-3 rounded-lg border border-gray-200">
              <div className="w-8 h-8 bg-gray-200 rounded-lg" />
              <div className="h-4 w-16 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <Building2 className="w-5 h-5 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">{t('cards.brands.title', 'Brands')}</h2>
      </div>
      
      {showGrid ? (
        <BrandGrid
          brands={brands}
          selectedBrand={selectedBrand}
          onBrandSelect={onBrandSelect}
        />
      ) : (
        <select
          value={selectedBrand}
          onChange={(e) => onBrandSelect(e.target.value)}
          className="w-full px-3 py-2 border border-black rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="" disabled>{t('cards.brands.selectPlaceholder', 'Select a brand...')}</option>
          {brands.map(brand => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};